/* eslint-disable */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

export function StoriesViewer({ user, isOpen, onClose, onNextUser, onPrevUser }) {
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const isPressingRef = useRef(false)
  const progressBarRef = useRef(null)
  const animationFrameRef = useRef(null)
  const storyStartedAtRef = useRef(0)
  const elapsedBeforePauseRef = useRef(0)

  const currentStory = user.stories[currentStoryIndex]
  const storyDuration = 5000 // 5 seconds per story

  const cancelProgressFrame = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
  }

  const setProgressBar = (value) => {
    if (progressBarRef.current) {
      progressBarRef.current.style.transform = `scaleX(${value / 100})`
    }
  }

  const resetStoryProgress = () => {
    cancelProgressFrame()
    elapsedBeforePauseRef.current = 0
    storyStartedAtRef.current = performance.now()
    setProgressBar(0)
  }

  // Reset when user changes
  useEffect(() => {
    setCurrentStoryIndex(0)
    resetStoryProgress()
  }, [user.id])

  useEffect(() => {
    resetStoryProgress()
  }, [currentStoryIndex])

  // Progress bar animation
  useEffect(() => {
    if (!isOpen || !currentStory) return undefined

    cancelProgressFrame()

    if (isPaused) {
      return undefined
    }

    storyStartedAtRef.current = performance.now() - elapsedBeforePauseRef.current

    const tick = (now) => {
      const elapsed = now - storyStartedAtRef.current
      const nextProgress = Math.min((elapsed / storyDuration) * 100, 100)

      elapsedBeforePauseRef.current = elapsed
      setProgressBar(nextProgress)

      if (nextProgress >= 100) {
        // Move to next story
        if (currentStoryIndex < user.stories.length - 1) {
          setCurrentStoryIndex(currentStoryIndex + 1)
        } else {
          // Last story of current user - move to next user or close
          if (onNextUser) {
            onNextUser()
          } else {
            // No more users, close the viewer
            onClose()
          }
        }
      } else {
        animationFrameRef.current = requestAnimationFrame(tick)
      }
    }

    animationFrameRef.current = requestAnimationFrame(tick)

    return cancelProgressFrame
  }, [isOpen, currentStoryIndex, user.stories.length, storyDuration, onClose, onNextUser, isPaused])

  const handleNext = () => {
    if (currentStoryIndex < user.stories.length - 1) {
      setCurrentStoryIndex(currentStoryIndex + 1)
      setIsPaused(false)
    } else if (onNextUser) {
      onNextUser()
    } else {
      // No more users, close the viewer
      onClose()
    }
  }

  const handlePrev = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(currentStoryIndex - 1)
      setIsPaused(false)
    } else if (onPrevUser) {
      onPrevUser()
    }
  }

  const handleMouseDown = () => {
    isPressingRef.current = true
    elapsedBeforePauseRef.current = Math.min(
      storyDuration,
      performance.now() - storyStartedAtRef.current,
    )
    setIsPaused(true)
  }

  const handleMouseUp = () => {
    if (isPressingRef.current) {
      isPressingRef.current = false
      setIsPaused(false)
    }
  }

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('touchend', handleMouseUp)
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('touchend', handleMouseUp)
    }
  }, [])

  // Take over the viewport: hide chrome, lock scroll
  useEffect(() => {
    if (!isOpen) return undefined

    const hiddenNodes = [
      ...document.querySelectorAll(
        'nav[class*="z-[1000]"], .fabits-bottom-nav, header.fixed, [class*="fixed top-0"][class*="z-[1000]"]',
      ),
    ]

    hiddenNodes.forEach((node) => {
      node.dataset.storiesPrevDisplay = node.style.display || ''
      node.style.display = 'none'
    })

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    return () => {
      hiddenNodes.forEach((node) => {
        node.style.display = node.dataset.storiesPrevDisplay || ''
        delete node.dataset.storiesPrevDisplay
      })
      document.body.style.overflow = previousOverflow
      document.documentElement.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen || !currentStory) return null

  return createPortal(
    <div
      className='fixed inset-0 z-[2147483000] h-[100dvh] w-screen max-h-[100dvh] max-w-[100vw] overflow-hidden bg-black'
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
      role='dialog'
      aria-modal='true'
      aria-label='Stories viewer'
    >
      <div className='absolute inset-0 z-0 h-full w-full bg-black' />

      {/* Progress bars */}
      <div className='absolute top-0 left-0 right-0 z-[100] flex gap-1 p-2 pt-[max(0.5rem,env(safe-area-inset-top))] md:p-3'>
        {user.stories.map((_, index) => (
          <div key={index} className='h-1 md:h-1.5 flex-1 bg-white/30 rounded-full overflow-hidden'>
            <div
              ref={index === currentStoryIndex ? progressBarRef : null}
              className='h-full origin-left bg-white will-change-transform'
              style={{
                transform:
                  index < currentStoryIndex
                    ? 'scaleX(1)'
                    : index === currentStoryIndex
                    ? 'scaleX(0)'
                    : 'scaleX(0)',
              }}
            />
          </div>
        ))}
      </div>

      {/* Close button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onClose()
        }}
        className='absolute top-[max(1rem,env(safe-area-inset-top))] right-4 z-[100] p-2 hover:bg-white/10 rounded-full transition-colors cursor-pointer'
        aria-label='Close stories'
      >
        <X className='w-6 h-6 text-white' />
      </button>

      {/* Story image container with click zones */}
      <div className='relative h-full w-full flex items-center justify-center'>
        <img
          src={currentStory.imageUrl || '/placeholder.svg'}
          alt='Story'
          className='relative z-10 h-full w-full object-cover pointer-events-none'
        />

        {/* Left zone - previous story */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            handlePrev()
          }}
          className='absolute left-0 top-0 h-full w-1/3 z-50 cursor-pointer'
          aria-label='Previous story'
        />

        {/* Center zone - pause on hold */}
        <div
          onMouseDown={(e) => {
            e.stopPropagation()
            handleMouseDown()
          }}
          onTouchStart={(e) => {
            e.stopPropagation()
            handleMouseDown()
          }}
          className='absolute left-1/2 top-0 h-full w-1/3 -translate-x-1/2 z-50 cursor-pointer'
        />

        {/* Right zone - next story */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            handleNext()
          }}
          className='absolute right-0 top-0 h-full w-1/3 z-50 cursor-pointer'
          aria-label='Next story'
        />
      </div>

      {/* Story counter */}
      <div className='absolute bottom-[max(1.5rem,env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-[100] text-white text-sm font-medium'>
        {currentStoryIndex + 1} / {user.stories.length}
      </div>
    </div>,
    document.body,
  )
}
